import { useState, useCallback } from 'react';
import type { LoadingStatus } from '../types/common';

/**
 * Custom hook for managing async operations
 * Provides loading states and error handling
 */
export interface UseAsyncReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  status: LoadingStatus;
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
}

export const useAsync = <T>(
  asyncFunction: (...args: any[]) => Promise<T>
): UseAsyncReturn<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<LoadingStatus>('idle');

  const execute = useCallback(
    async (...args: any[]): Promise<T | null> => {
      setLoading(true);
      setStatus('loading');
      setError(null);

      try {
        const result = await asyncFunction(...args);
        setData(result);
        setStatus('success');
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        setStatus('error');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [asyncFunction]
  );

  const reset = useCallback(() => {
    setData(null);
    setLoading(false);
    setError(null);
    setStatus('idle');
  }, []);

  return {
    data,
    loading,
    error,
    status,
    execute,
    reset,
  };
};

export default useAsync;
